import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
} from "@angular/common/http";
import { NgModule } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatDialogModule } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideMessaging, getMessaging } from '@angular/fire/messaging';

import { EffectsModule } from "@ngrx/effects";
import { StoreRouterConnectingModule } from "@ngrx/router-store";
import { StoreModule } from "@ngrx/store";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";

import { environment } from "../environments/environment";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import * as fromGuards from "./guards";
import * as fromServices from "./services";
import * as fromStore from "./store";
import { CustomSerializer } from "./utils/custom-serializer";
import { LottieAnimationModule } from "./shared-modules/lottie-animation/lottie-animation.module";
import { SharedModule } from "./shared/shared.module";
import {
  ScreenTrackingService,
  UserTrackingService,
} from "@angular/fire/analytics";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateLSLoader } from "./models/i18n";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { TranslatePaginatorIntl } from "./services/translate-paginator-intl";
import { getDatabase, provideDatabase } from "@angular/fire/database";
import { getAuth, provideAuth } from "@angular/fire/auth";

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    TranslateModule.forRoot({
      defaultLanguage: "en",
      loader: {
        provide: TranslateLoader,
        useClass: TranslateLSLoader,
      },
    }),
    AppRoutingModule,
    HttpClientModule,
    MatSnackBarModule,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    SharedModule,
    LottieAnimationModule,
    // Replacing AngularFireModule and AngularFireMessagingModule
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideMessaging(() => getMessaging()),
    provideDatabase(() => getDatabase()),
    // QSL-10805: register Firebase Auth so the web app can sign in anonymously
    // (required for RTDB reads once rules move to ".read": "auth != null").
    provideAuth(() => getAuth()),

    StoreModule.forRoot(fromStore.reducers, {
      metaReducers: [fromStore.clearState],
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
        strictStateSerializability: false,
        strictActionSerializability: false,
      },
    }),

    EffectsModule.forRoot(fromStore.EFFECTS),
    StoreRouterConnectingModule.forRoot({
      serializer: CustomSerializer,
    }),
    // QSL-10648: Redux DevTools must NOT ship to production. Previously it was
    // instrument()-ed in EVERY build (only logOnly-gated), so @ngrx/store-devtools
    // stayed in the vendor bundle AND actively retained a maxAge action/state history
    // in memory — a real footprint on the low-RAM devices whose Siraj WebView OOM-freezes
    // during bootstrap. Include it only in non-prod builds; Angular flattens the empty
    // array away and the prod optimizer tree-shakes the module out (ngrx-recommended,
    // AOT-safe pattern).
    environment.production
      ? []
      : StoreDevtoolsModule.instrument({ maxAge: 25 }),
    BrowserAnimationsModule,
    DragDropModule,
  ],
  providers: [
    ...fromGuards.GUARDS,
    ...fromServices.SERVICES,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: fromServices.TokenInterceptor,
      multi: true,
    },
    ScreenTrackingService,
    UserTrackingService,
    TranslateService,
    { provide: MatPaginatorIntl, useClass: TranslatePaginatorIntl },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
